import { useEffect, useRef, useState } from 'react'
import { api, ApiError, can, type IncidentCorrection, type IncidentDetail } from '../lib/api'
import { CorrectCall } from './CorrectCall'
import { PlaceMap } from './PlaceMap'
import { PRIORITIES } from './Intake'
import { CallbackRows, CallerRows, SendRows, ThreadRows, hhmm, type Sends } from './CallParts'
import { CallNotes } from './CallNotes'
import './dispatch.css'

type PastCall = Awaited<ReturnType<typeof api.pastCalls>>['calls'][number]

/** What each corrected thing is called (078, 079). */
const FIX_WORDS: Record<string, string> = {
  called_at: 'Call in', accepted_at: 'Took it', on_scene_at: 'On scene', covered_at: 'Covered',
  closed_at: 'Closed', added: 'Added afterwards', caller_name: 'Caller name',
  caller_phone: 'Called from', relationship: 'Calling for', caller_on_scene: 'Staying on scene',
  caller_urgency: 'Caller urgency', anonymous: 'Anonymous', priority: 'Urgency',
  address: 'Address', location_details: 'Location details', cross_streets: 'Between',
  location_note: 'Directions', nature: 'Nature', resolution: 'Outcome',
  outcome_note: 'Closing note', answer: 'Answer', other_number: 'Other number',
  pin: 'Map pin', broadcast_area: 'Area', removed: 'Taken off', role: 'Role',
  internal_note: 'Internal note', responder_note: 'Note for the responder',
}

/** A corrected note's words can run to pages (081): the table shows the
 *  start; the note itself is in the Notes box above. */
const clip = (text: string | null | undefined) =>
  text == null ? 'none' : text.length > 240 ? `${text.slice(0, 240)}…` : text

const PRIORITY: Record<number, string> = {
  1: 'Critical', 2: 'Urgent', 3: 'Normal', 4: 'Low', 5: 'Routine',
}

function when(v: string | null | undefined): string {
  return v
    ? new Date(v).toLocaleString([], {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    })
    : '—'
}

/** A bare date ("2026-10-26") as a day, read as that calendar day -- never
 *  as UTC midnight, which west of Greenwich is the day before. */
function dayText(iso: string, year = true): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString([], {
    ...(year ? { year: 'numeric' } : {}), month: 'short', day: 'numeric' })
}

/** When it came in -- or its DAY alone when the time is not known (083):
 *  the column then holds the start of that day on the organization's
 *  clock, which nobody gave; the server says which day (`called_day`). */
function cameIn(c: { called_at: string; called_at_unknown?: boolean; called_day?: string | null }): string {
  if (!c.called_at_unknown) return when(c.called_at)
  return `${c.called_day ? dayText(c.called_day) : new Date(c.called_at).toLocaleDateString([], {
    year: 'numeric', month: 'short', day: 'numeric' })} · time not known`
}

/** PAST CALLS: every closed call, read-only, for whoever holds the tick.
 *
 *  The customer, 16 Sep 2026: "once the call is closed no one has access to
 *  history calls, only administrators ... to review and do reports ... we need
 *  to be able to open up all those calls and review them, not editing them
 *  because it's already closed, but maybe put our own input notes on a closed
 *  call." So there is exactly one thing here that writes: a review note, which
 *  is added to the history under the reviewer's name and never changes what is
 *  already there. The server enforces every part of that (`calls.past.review`),
 *  not this screen. */
export function PastCalls({ openId }: { openId?: string | null }) {
  const [q, setQ] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [calls, setCalls] = useState<PastCall[]>([])
  const [more, setMore] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [chosen, setChosen] = useState<string | null>(openId ?? null)
  /** Whether a search has ANSWERED yet. "No closed calls match" before the
   *  first answer is a confident wrong statement. */
  const [loaded, setLoaded] = useState(false)
  /** Every request takes a number; only the latest one's answer lands. Typing
   *  while "Older calls" is loading would otherwise append the unfiltered page
   *  under the filtered list. */
  const ask = useRef(0)

  useEffect(() => { if (openId) setChosen(openId) }, [openId])

  // DEBOUNCED, and the ANSWER FOR OLD TYPING IS DROPPED: two searches in flight
  // can land out of order, and the list would flick back to "INC-1".
  useEffect(() => {
    const mine = ++ask.current
    const timer = setTimeout(async () => {
      setBusy(true)
      try {
        const found = await api.pastCalls({
          q, date_from: dateFrom || null, date_to: dateTo || null,
        })
        if (ask.current !== mine) return
        setCalls(found.calls)
        setMore(found.more)
        setLoaded(true)
        setError(null)
      } catch (e) {
        if (ask.current === mine) {
          setError(e instanceof ApiError ? e.message : 'Could not load past calls.')
        }
      } finally {
        if (ask.current === mine) setBusy(false)
      }
    }, 250)
    return () => { clearTimeout(timer) }
  }, [q, dateFrom, dateTo])

  async function loadMore() {
    const last = calls.at(-1)
    if (!last) return
    const mine = ++ask.current
    setBusy(true)
    try {
      const found = await api.pastCalls({
        q, date_from: dateFrom || null, date_to: dateTo || null, before: last.called_at,
        // THE TIE, TOO (083 review): calls whose time is not known share
        // their day's start, and a page may end between two of them.
        before_id: last.id,
      })
      if (ask.current !== mine) return
      setCalls((held) => [...held, ...found.calls])
      setMore(found.more)
    } catch (e) {
      if (ask.current === mine) setError(e instanceof ApiError ? e.message : 'Could not load more.')
    } finally {
      if (ask.current === mine) setBusy(false)
    }
  }

  return (
    <div className="pastcalls">
      <div className="pc-list">
        <h2>Past calls</h2>
        <input
          className="searchbox"
          value={q}
          placeholder="Number, name, address, INC-10…"
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="pc-dates">
          <label>From <input type="date" value={dateFrom}
                             onChange={(e) => setDateFrom(e.target.value)} /></label>
          <label>To <input type="date" value={dateTo}
                           onChange={(e) => setDateTo(e.target.value)} /></label>
        </div>
        {error && <div className="notice">{error}</div>}
        <div className="pc-rows">
          {calls.length === 0 && loaded && !busy && !error && (
            <div className="dim" style={{ padding: 12 }}>No closed calls match.</div>
          )}
          {calls.map((c) => (
            <button
              type="button"
              key={c.id}
              className={`pc-row ${chosen === c.id ? 'on' : ''}`}
              onClick={() => setChosen(c.id)}
            >
              <span className="top">
                <span className="mono">{c.number}</span>
                <span className="nm">{c.nature_label}</span>
                <span className="dim">{cameIn(c)}</span>
              </span>
              {(c.logged_afterwards || c.corrected) && (
                <span className="pc-marks">
                  {c.logged_afterwards && <span className="pill lgc-own">logged afterwards</span>}
                  {(c.called_at_unknown || c.closed_at_unknown) && <span className="pill lgc-own">times not known</span>}
                  {c.corrected && <span className="pill ccor-ed">corrected</span>}
                </span>
              )}
              <span className="sub">
                {c.location ?? 'No address'}
                {c.caller_name ? ` · ${c.caller_name}` : ''}
                {c.resolution_label ? ` · ${c.resolution_label}` : ''}
              </span>
            </button>
          ))}
          {more && (
            <button type="button" className="btn sm" disabled={busy} onClick={loadMore}>
              {busy ? 'Loading…' : 'Older calls'}
            </button>
          )}
        </div>
      </div>
      <div className="pc-record">
        {chosen
          ? <CallRecord key={chosen} id={chosen} />
          : <div className="work-empty"><h3>Pick a call to review it</h3></div>}
      </div>
    </div>
  )
}

/** One closed call, read-only, with everything that happened. */
function CallRecord({ id }: { id: string }) {
  const [call, setCall] = useState<IncidentDetail | null>(null)
  const [sends, setSends] = useState<Sends>([])
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [posting, setPosting] = useState(false)
  /** "Correct this call" is open (078), and what the last save said. */
  const [correcting, setCorrecting] = useState(false)
  const [corrected, setCorrected] = useState<string | null>(null)

  async function load() {
    try {
      const [detail, sent] = await Promise.all([api.incident(id), api.incidentBroadcasts(id)])
      setCall(detail)
      setSends(sent)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open that call.')
    }
  }
  useEffect(() => { load() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  /** THE ONE WRITE ON THIS SCREEN. The words stay in the box until the server
   *  has them, so a refusal never costs the typing. */
  async function addReview() {
    const text = note.trim()
    if (!text || posting) return
    setPosting(true)
    try {
      await api.incidentNote(id, text)
      setNote('')
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add that note.')
    } finally {
      setPosting(false)
    }
  }

  if (error && !call) return <div className="wbody"><div className="notice">{error}</div></div>
  if (!call) return <div className="wbody dim">Loading…</div>

  const now = call.locations.at(-1)
  const fixes = call.corrections ?? []
  /** THE FIRST VALUE A TIME HAD (078): the oldest correction of it says what
   *  it was before anybody touched it -- "corrected · was 21:52". */
  const wasOf = (field: string, responder: string | null = null): IncidentCorrection | undefined =>
    fixes.find((f) => f.field === field && f.responder_id === responder)
  const steps: [string, string | null, IncidentCorrection | undefined][] = [
    ['Call in', call.called_at_unknown ? 'not known' : hhmm(call.called_at), wasOf('called_at')],
    ['Broadcast', hhmm(call.broadcast_at), undefined],
    ['Accepted', hhmm(call.accepted_at), wasOf('accepted_at')],
    ['On scene', hhmm(call.on_scene_at), wasOf('on_scene_at')],
    ['Covered', hhmm(call.covered_at), wasOf('covered_at')],
    ['Closed', call.closed_at_unknown ? 'not known' : hhmm(call.closed_at), wasOf('closed_at')],
  ]
  /** What a corrected time was. A came-in or closed time that was not known
   *  (083) was nothing; a call moved to another day with its time still not
   *  known was on another DAY. */
  const wasText = (label: string, fix: IncidentCorrection): string => {
    if (fix.subject === 'day' && fix.was) {
      return `day was ${new Date(fix.was).toLocaleDateString([], { month: 'short', day: 'numeric' })}, time not known`
    }
    return `was ${hhmm(fix.was) ?? (label === 'Call in' || label === 'Closed' ? 'not known' : 'none')}`
  }
  /** A correction's value in the table: a day moved with its time not known
   *  (083) is two days, never two midnights; a time not known is said. */
  const fixValue = (f: IncidentCorrection, v: string | null): string => {
    if (f.subject === 'day') return v ? `${new Date(v).toLocaleDateString([], { month: 'short', day: 'numeric' })}, time not known` : 'not known'
    if (f.field === 'added') return v ? `took it ${hhmm(v)}` : 'time not recorded'
    return hhmm(v) ?? (f.field === 'called_at' || f.field === 'closed_at' ? 'not known' : 'none')
  }

  return (
    <>
      <div className="wh">
        <div>
          <div className="num">
            {call.number} · {cameIn(call)} · {call.source}
            {call.logged_after_outage && ' · logged after an outage'}
            {call.logged_afterwards && <> <span className="pill lgc-own">Logged afterwards</span></>}
            {fixes.length > 0 && <> <span className="pill ccor-ed">Corrected</span></>}
          </div>
          <h1>
            {call.call_code && <><span className="callcode big">{call.call_code}</span> </>}
            {call.nature_label}
          </h1>
          <div className="sub">
            {call.anonymous && <span className="anonpill">Anonymous · not to be given out</span>}
            {call.caller_name ?? 'Unknown caller'}
            {call.relationship_label && ` · calling for ${call.relationship_label.toLowerCase()}`}
            {` · ${PRIORITY[call.priority] ?? `priority ${call.priority}`}`}
          </div>
        </div>
        <div className="right">
          <span className="pill">{call.status === 'closed' ? 'Closed' : call.status}</span>
          {call.resolution_label && <b>{call.resolution_label}</b>}
          {/* CORRECT THIS CALL (078): its own tick, on a closed call only. */}
          {call.status === 'closed' && can('calls.past.correct') && !correcting && (
            <button type="button" className="btn sm ccor-open"
                    onClick={() => { setCorrected(null); setCorrecting(true) }}>
              Correct this call
            </button>
          )}
        </div>
      </div>

      <div className="wbody">
        {error && <div className="notice">{error}</div>}
        {corrected && <div className="banner cb-done" role="status">{corrected}</div>}
        {correcting && (
          <CorrectCall
            call={call}
            onCancel={() => setCorrecting(false)}
            onSaved={async (r) => {
              setCorrecting(false)
              setCorrected(`Saved — ${r.changes} change${r.changes === 1 ? '' : 's'}, each kept with what it was.`)
              await load()
            }}
          />
        )}
        {call.outcome_note && (
          <div className="box">
            <h4>How it ended</h4>
            <div className="bd">{call.outcome_note}</div>
          </div>
        )}

        <div className="tl" style={{ marginTop: 15 }}>
          {steps.map(([label, value, fix]) => (
            <div className={`st ${value ? 'done' : ''}`} key={label}>
              <div className="lb">{label}</div>
              <div className="vl">{value ?? '— —'}</div>
              {fix && (
                <div className="ccor-was" title={`Corrected by ${fix.corrected_by_label}: ${fix.reason}`}>
                  corrected · {wasText(label, fix)}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="wgrid2" style={{ marginTop: 15 }}>
          <div className="box">
            <h4>Caller</h4>
            <div className="bd">
              <dl className="kv">
                <dt>Name</dt><dd>{call.caller_name ?? '—'}</dd>
                {call.caller_urgency != null && (
                  <>
                    <dt>Caller urgency</dt>
                    <dd>{PRIORITIES.find((p) => p.v === call.caller_urgency)?.label ?? call.caller_urgency}</dd>
                  </>
                )}
                {/* SAVE ONE TO THE PHONE BOOK (25 Oct 2026): the
                    administrator going through the old calls himself. The
                    call is not changed. */}
                <CallbackRows callerPhone={call.caller_phone}
                              numbers={call.callback_numbers ?? []}
                              callerName={call.anonymous ? null : call.caller_name}
                              onFile={can('phonebook.add')
                                ? async (n, save, name) => {
                                  await api.incidentCallbackFile(call.id, n.id, save, name)
                                  await load()
                                }
                                : undefined} />
                <dt>Calling for</dt><dd>{call.relationship_label ?? '—'}</dd>
                <dt>On scene</dt>
                <dd>{call.caller_on_scene === false ? 'No — left the scene'
                  : call.caller_on_scene ? 'Yes' : '—'}</dd>
              </dl>
            </div>
          </div>
          <CallerRows call={call} />
          <div className="box">
            <h4>Location</h4>
            <div className="bd">
              <dl className="kv">
                <dt>At</dt><dd>{now?.label ?? '—'}</dd>
                {now?.cross_street_a && now?.cross_street_b && (
                  <><dt>Between</dt><dd>{now.cross_street_a} and {now.cross_street_b}</dd></>
                )}
                {call.broadcast_area && (
                  <><dt>Broadcast as</dt><dd>{call.broadcast_area}</dd></>
                )}
                {call.locations.length > 1 && (
                  <><dt>Started at</dt><dd>{call.locations[0].label}</dd></>
                )}
              </dl>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 15 }}>
          <PlaceMap place={call.place} pinned={call.pinned} readOnly />
        </div>

        {call.answers.length > 0 && (
          <div className="box" style={{ marginTop: 15 }}>
            <h4>Answers</h4>
            <div className="bd">
              <dl className="kv">
                {call.answers.map((a) => (
                  <div key={a.id} style={{ display: 'contents' }}>
                    <dt>{a.prompt}</dt>
                    <dd style={a.is_alert ? { color: 'var(--alarm)' } : undefined}>
                      {a.answer}
                      {a.responder_note && <div className="dim">Note: {a.responder_note}</div>}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}

        {/* THE NOTES (081), read-only, in their own colours. */}
        {(call.call_notes ?? []).length > 0 && (
          <div className="box" style={{ marginTop: 15 }}>
            <h4>Notes</h4>
            <div className="bd">
              <CallNotes callId={call.id} notes={call.call_notes ?? []} readOnly />
            </div>
          </div>
        )}

        <div className="box" style={{ marginTop: 15 }}>
          <h4>Responders <span className="pill">{call.responders.filter((r) => !r.removed_at).length}</span></h4>
          {call.responders.length === 0 ? (
            <div className="bd dim">Nobody was logged as going.</div>
          ) : call.responders.map((r) => r.removed_at ? (
            /* TAKEN OFF, LOGGED BY MISTAKE (080): crossed out, counted nowhere. */
            <div className="resp rmv-off" key={r.id}>
              <span className="cs">{r.who}</span>
              <div className="nm">
                <s>{r.who}</s>
                <small>
                  {r.role} · {r.accepted_at ? `accepted ${hhmm(r.accepted_at)}` : 'time not recorded'} · removed {hhmm(r.removed_at)}
                  {' by '}{r.removed_by_label ?? 'a dispatcher'} — {r.removed_reason ?? 'logged by mistake'}
                </small>
              </div>
              <span className="pill rmv-pill">removed</span>
            </div>
          ) : (
            <div className="resp" key={r.id}>
              <span className="cs">{r.who}</span>
              <div className="nm">
                {r.who}
                <small>
                  {r.role} · {r.accepted_at ? `accepted ${hhmm(r.accepted_at)}` : 'time not recorded'} · {r.accepted_via}
                  {r.on_scene_at && ` · on scene ${hhmm(r.on_scene_at)}`}
                  {r.released_at && ` · no longer going ${hhmm(r.released_at)}`}
                </small>
              </div>
              {/* WHAT A CORRECTION DID TO HIM (078): added afterwards, or a
                  time changed -- each with what it was. */}
              {wasOf('added', r.id)
                ? <span className="pill ccor-ed">added afterwards</span>
                : (wasOf('accepted_at', r.id) || wasOf('on_scene_at', r.id)) && (
                  <span className="pill ccor-ed">
                    corrected
                    {wasOf('accepted_at', r.id) && ` · took it was ${hhmm(wasOf('accepted_at', r.id)!.was) ?? 'not recorded'}`}
                    {wasOf('on_scene_at', r.id)
                      && ` · arrived was ${hhmm(wasOf('on_scene_at', r.id)!.was) ?? 'none'}`}
                  </span>
                )}
            </div>
          ))}
        </div>

        {sends.length > 0 && (
          <div className="box" style={{ marginTop: 15 }}>
            <h4>Messages sent <span className="pill">{sends.length}</span></h4>
            <SendRows sends={sends} />
          </div>
        )}

        {/* EVERY CORRECTION, WITH WHAT IT WAS (078, 079): the record says what
            is not the original, and who changed it and why. */}
        {fixes.length > 0 && (
          <div className="box" style={{ marginTop: 15 }}>
            <h4>Corrections <span className="pill ccor-ed">{fixes.length}</span></h4>
            <div className="bd">
              <table className="lgc-who">
                <thead><tr><th>What</th><th>Was</th><th>Became</th><th>By</th><th>Why</th></tr></thead>
                <tbody>
                  {fixes.map((f, i) => {
                    const who = f.responder_id ? call.responders.find((r) => r.id === f.responder_id)?.who : null
                    const timed = f.was_text == null && f.became_text == null
                    const what = f.subject === 'day' ? 'Day it came in'
                      : f.subject || FIX_WORDS[f.field] || f.field
                    return (
                      <tr key={i}>
                        <td>{who ? `${who} · ` : ''}{what}</td>
                        <td className="dim">{timed ? (f.field === 'added' ? '—' : fixValue(f, f.was)) : clip(f.was_text)}</td>
                        <td>{timed ? fixValue(f, f.became) : clip(f.became_text)}</td>
                        <td>{f.corrected_by_label}<div className="dim">{when(f.corrected_at)}</div></td>
                        <td>{f.reason}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="box" style={{ marginTop: 15 }}>
          <h4>History</h4>
          <div className="bd">
            <ThreadRows thread={call.thread} />
          </div>
          <div className="bd" style={{ borderTop: '1px solid var(--line-soft)' }}>
            <textarea
              className="orgpick"
              style={{ display: 'block', width: '100%', maxWidth: 'none', resize: 'vertical' }}
              rows={2}
              maxLength={4000}
              value={note}
              aria-label="Add a review note"
              placeholder="Add a review note. It is added to the history with your name; nothing above changes."
              onChange={(e) => setNote(e.target.value)}
            />
            <button className="btn primary" style={{ marginTop: 8 }}
                    disabled={!note.trim() || posting} onClick={addReview}>
              {posting ? 'Adding…' : 'Add review note'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
